import * as THREE from "three";
import { IController, StopBehavior } from "../types/controller_types.js";
import { IVfxTask } from "../runtime/vfx_task_runner.js";

export interface IEmitter3DConfig
{
  rate_over_time?: number;
  rate_over_distance?: number;
  count?: number;
}

export class Emitter implements IVfxTask, IController
{
  public is_active = true;

  protected readonly _position: THREE.Vector3;
  public get position(): THREE.Vector3 { return this._position; }

  protected readonly _last_position: THREE.Vector3;
  protected readonly _move_dir = new THREE.Vector3( 0, 0, 1 );
  public get move_dir(): THREE.Vector3 { return this._move_dir; }

  private fraction_distance = 0;
  private last_time = 0;

  public readonly rate_over_distance: number;
  public readonly rate_over_time: number;
  public readonly count: number;

  private readonly time_step: number = 0;
  private last_emit_time: number = 0;

  private sub_controllers: IController[] = [];

  private static readonly scratch_spawn_pos = new THREE.Vector3();

  constructor( position: THREE.Vector3, config?: IEmitter3DConfig )
  {
    this._position = position.clone();
    this._last_position = position.clone();
    this.rate_over_distance = config?.rate_over_distance ?? 0;
    this.rate_over_time = config?.rate_over_time ?? 0;
    this.count = config?.count ?? 1;
    
    if ( this.rate_over_time > 0 )
    {
      this.time_step = 1.0 / this.rate_over_time; // Assuming time is in seconds
    }
  }

  public addSubController( controller: IController ): void
  {
    this.sub_controllers.push( controller );
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    const diff = new THREE.Vector3().subVectors( pos, this._position );
    if ( diff.lengthSq() > 0.00001 )
    {
      this._move_dir.copy( diff ).normalize();
    }
    this._position.copy( pos );

    for ( const sub of this.sub_controllers )
      sub.setPosition( pos );
  }

  public stop( behavior?: StopBehavior ): void
  {
    this.is_active = false;
    for ( const sub of this.sub_controllers )
      sub.stop( behavior );
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( factor: number ): void {}

  protected emitParticle( spawn_pos: THREE.Vector3, move_dir: THREE.Vector3, now: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    if ( this.last_time === 0 )
    {
      this.last_time = now;
      if ( this.time_step > 0 ) this.last_emit_time = now;
      this._last_position.copy( this._position );
      return false;
    }

    const dt = now - this.last_time;
    this.last_time = now;

    // 1. Rate Over Time
    if ( this.time_step > 0 )
    {
      let time_passed = now - this.last_emit_time;
      // Prevent massive spiral of death if tab was inactive
      if ( time_passed > 1.0 ) 
      {
        time_passed = 1.0;
        this.last_emit_time = now - 1.0;
      }

      while ( time_passed >= this.time_step )
      {
        this.last_emit_time += this.time_step;
        time_passed -= this.time_step;

        for ( let c = 0; c < this.count; c++ )
          this.emitParticle( this._position, this._move_dir, this.last_emit_time );
      }
    }

    // 2. Rate Over Distance (Interpolated)
    const dist = this._position.distanceTo( this._last_position );

    if ( this.rate_over_distance > 0 && dist > 0.0001 )
    {
      this.fraction_distance += dist * this.rate_over_distance;
      const spawn_count = Math.floor( this.fraction_distance );
      
      if ( spawn_count > 0 )
      {
        this.fraction_distance -= spawn_count;
        
        // Step fraction per particle
        const step = 1.0 / spawn_count;
        
        for ( let i = 0; i < spawn_count; i++ )
        {
          // Calculate interpolation factor t from 0.0 to 1.0
          // E.g. for 2 particles, t will be 0.5 and 1.0
          const t = ( i + 1 ) * step; 
          
          Emitter.scratch_spawn_pos.lerpVectors( this._last_position, this._position, t );
          
          for ( let c = 0; c < this.count; c++ )
          {
            this.emitParticle( Emitter.scratch_spawn_pos, this._move_dir, now );
          }
        }
      }
    }
    
    this._last_position.copy( this._position );

    return false;
  }
}
